import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Bell, DollarSign, Target } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { SEO } from '../../components/common/SEO';
import { useCanonicalUrl } from '../../hooks/useCanonicalUrl';

export const PriceTrackerPage: React.FC = () => {
  const canonicalUrl = useCanonicalUrl();

  return (
    <>
      <SEO
        title="Price Tracker - Never Miss a Sale Again"
        description="Track prices on your favorite items and get notified when they drop. Save money on gifts and purchases with our intelligent price tracking system."
        keywords={['price tracker', 'price alerts', 'sale notifications', 'discount tracker', 'price monitoring']}
        url={canonicalUrl}
      />

      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Price Tracker
            </h1>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Never miss a sale again. Track prices on your favorite items and get 
              instant notifications when prices drop. Save money on every purchase.
            </p>
            <Link to="/dashboard">
              <Button size="lg">
                Start Tracking Prices
                <TrendingUp className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>

          {/* Features */}
          <section className="mb-12">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="text-center">
                <Bell className="h-12 w-12 text-blue-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Instant Alerts</h3>
                <p className="text-gray-600">
                  Get notified immediately when prices drop on your tracked items.
                </p>
              </Card>

              <Card className="text-center">
                <DollarSign className="h-12 w-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Save Money</h3>
                <p className="text-gray-600">
                  Track price history and buy at the perfect time to maximize savings.
                </p>
              </Card>

              <Card className="text-center">
                <Target className="h-12 w-12 text-purple-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-3">Smart Tracking</h3>
                <p className="text-gray-600">
                  Our AI monitors prices across multiple retailers for the best deals.
                </p>
              </Card>
            </div>
          </section>

          {/* How It Works */}
          <section className="mb-12">
            <Card className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">How It Works</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="bg-brown-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-brown-500">1</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Add Items</h3>
                  <p className="text-gray-600">
                    Add products from your wishlist or paste any product URL.
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-brown-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-brown-500">2</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Set Target Price</h3>
                  <p className="text-gray-600">
                    Choose your ideal price point and we'll monitor for drops.
                  </p>
                </div>
                <div className="text-center">
                  <div className="bg-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-2xl font-bold text-green-600">3</span>
                  </div>
                  <h3 className="text-lg font-semibold mb-2">Get Notified</h3>
                  <p className="text-gray-600">
                    Receive alerts via email or SMS when prices hit your target.
                  </p>
                </div>
              </div>
            </Card>
          </section>

          {/* Benefits */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Why Use Price Tracking?</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <Card>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Save on Gifts</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Track expensive items for special occasions</li>
                  <li>• Buy gifts during sales and promotions</li>
                  <li>• Never pay full price for wishlist items</li>
                  <li>• Plan purchases around seasonal discounts</li>
                </ul>
              </Card>
              <Card>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Smart Shopping</h3>
                <ul className="space-y-2 text-gray-600">
                  <li>• Compare prices across retailers</li>
                  <li>• Track price history and trends</li>
                  <li>• Identify the best time to buy</li>
                  <li>• Avoid impulse purchases at high prices</li>
                </ul>
              </Card>
            </div>
          </section>

          {/* Tips */}
          <section className="mb-12">
            <Card className="p-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">Price Tracking Tips</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Setting Target Prices</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Research historical prices first</li>
                    <li>• Set realistic target prices (10-30% off)</li>
                    <li>• Consider seasonal patterns</li>
                    <li>• Factor in shipping and taxes</li>
                  </ul>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Best Times to Buy</h3>
                  <ul className="space-y-2 text-gray-600">
                    <li>• Black Friday and Cyber Monday</li>
                    <li>• End-of-season clearances</li>
                    <li>• Amazon Prime Day</li>
                    <li>• Back-to-school sales</li>
                  </ul>
                </div>
              </div>
            </Card>
          </section>

          {/* CTA */}
          <section className="text-center">
            <Card className="p-8 bg-white">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Start Tracking Prices Today
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Add items to your wishlist and start saving money with price tracking.
              </p>
              <Link to="/dashboard">
                <Button size="lg">
                  <TrendingUp className="h-5 w-5 mr-2" />
                  Create Wishlist
                </Button>
              </Link>
            </Card>
          </section>
        </div>
      </div>
    </>
  );
};
